import React, { createContext, useContext, useState } from 'react';
import { supabase, Session, User, BlogIdea } from '../lib/supabase';

interface SessionContextType {
  session: Session | null;
  user: User | null;
  currentIdea: BlogIdea | null;
  loading: boolean;
  error: string | null;
  loadSessionById: (sessionId: string) => Promise<void>;
  createAuthenticatedSession: (userId: string) => Promise<string | null>;
  updateSession: (updates: Partial<Session>) => Promise<void>;
  refreshSession: () => Promise<void>;
  resetSession: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [currentIdea, setCurrentIdea] = useState<BlogIdea | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const updateSession = async (updates: Partial<Session>) => {
    if (!session) return;

    try {
      const { data, error } = await supabase
        .from('sessions')
        .update(updates)
        .eq('id', session.id)
        .select()
        .maybeSingle();

      if (error) {
        console.error('updateSession error:', error);
      } else if (data) {
        setSession(data);
      }
    } catch (err) {
      console.error('Error updating session:', err);
    }
  };

  const refreshSession = async () => {
    if (!session) return;

    try {
      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', session.id)
        .maybeSingle();

      if (sessionError) {
        console.error('refreshSession error:', sessionError);
      } else if (sessionData) {
        setSession(sessionData);

        if (sessionData.state === 'idea_selection') {
          const { data: ideaData } = await supabase
            .from('blog_ideas')
            .select('*')
            .eq('session_id', sessionData.id)
            .eq('status', 'presented')
            .order('presented_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          setCurrentIdea(ideaData);
        } else if (sessionData.approved_idea_id) {
          const { data: ideaData } = await supabase
            .from('blog_ideas')
            .select('*')
            .eq('id', sessionData.approved_idea_id)
            .maybeSingle();

          setCurrentIdea(ideaData);
        }
      }
    } catch (err) {
      console.error('Error refreshing session:', err);
    }
  };

  const loadSessionById = async (sessionId: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (sessionError || !sessionData) {
        setError('Deze sessie is ongeldig of verlopen.');
        setLoading(false);
        return;
      }

      setSession(sessionData);

      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', sessionData.user_id)
        .maybeSingle();

      setUser(userData);

      if (sessionData.state === 'idea_selection') {
        const { data: ideaData } = await supabase
          .from('blog_ideas')
          .select('*')
          .eq('session_id', sessionData.id)
          .eq('status', 'presented')
          .order('presented_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        setCurrentIdea(ideaData);
      } else if (sessionData.approved_idea_id) {
        const { data: ideaData } = await supabase
          .from('blog_ideas')
          .select('*')
          .eq('id', sessionData.approved_idea_id)
          .maybeSingle();

        setCurrentIdea(ideaData);
      }

      await supabase
        .from('sessions')
        .update({ last_activity: new Date().toISOString() })
        .eq('id', sessionData.id);

      setLoading(false);
    } catch (err) {
      console.error('Error loading session by ID:', err);
      setError('Er ging iets mis bij het laden van je sessie.');
      setLoading(false);
    }
  };

  const createAuthenticatedSession = async (userId: string): Promise<string | null> => {
    try {
      const oneHourAgo = new Date();
      oneHourAgo.setHours(oneHourAgo.getHours() - 1);

      const { data: existingSession, error: checkError } = await supabase
        .from('sessions')
        .select('id, created_at')
        .eq('user_id', userId)
        .eq('state', 'idea_selection')
        .gte('created_at', oneHourAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking existing sessions:', checkError);
      }

      if (existingSession) {
        console.log('Reusing existing active session:', existingSession.id);
        return existingSession.id;
      }

      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .insert({
          user_id: userId,
          state: 'idea_selection',
          reminder_count: 0,
        })
        .select()
        .single();

      if (sessionError) {
        console.error('Error creating session:', sessionError);
        throw new Error('Kon sessie niet aanmaken');
      }

      return sessionData.id;
    } catch (err) {
      console.error('Error in createAuthenticatedSession:', err);
      return null;
    }
  };

  const resetSession = async () => {
    if (!session || !user) return;

    try {
      if (currentIdea) {
        await supabase
          .from('blog_ideas')
          .update({
            status: 'rejected',
            rejection_reason: 'Gebruiker begon opnieuw',
            decided_at: new Date().toISOString(),
          })
          .eq('id', currentIdea.id);
      }

      const { data: previouslyRejectedIdeas } = await supabase
        .from('blog_ideas')
        .select('idea_title, rejection_reason')
        .eq('session_id', session.id)
        .eq('status', 'rejected')
        .order('presented_at', { ascending: true });

      const previousTopicsContext = Array.isArray(previouslyRejectedIdeas) && previouslyRejectedIdeas.length > 0
        ? previouslyRejectedIdeas
            .map((idea, index) =>
              `${index + 1}. "${idea.idea_title}" - ${idea.rejection_reason || 'Geen reden opgegeven'}`
            )
            .join('\n')
        : '';

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-generate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'idea',
            session_id: session.id,
            user_id: user.id,
            data: {
              previous_topics: previousTopicsContext,
            },
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'AI kon geen idee genereren');
      }

      const { error: insertError } = await supabase.from('blog_ideas').insert({
        session_id: session.id,
        user_id: user.id,
        source: 'ai_regenerated',
        idea_title: data.result.title,
        idea_content: data.result.content,
        idea_reason: data.result.reason || '',
        status: 'presented',
      });

      if (insertError) {
        throw new Error('Kon nieuw idee niet opslaan');
      }

      const { error: resetError } = await supabase
        .from('sessions')
        .update({
          state: 'idea_selection',
          approved_idea_id: null,
          article_content: null,
          titles: null,
          intros: null,
          images: null,
          selected_title: null,
          selected_intro: null,
          selected_image_url: null,
        })
        .eq('id', session.id);

      if (resetError) {
        throw resetError;
      }

      await refreshSession();
    } catch (err) {
      console.error('Error in resetSession:', err);
      throw err;
    }
  };

  return (
    <SessionContext.Provider
      value={{
        session,
        user,
        currentIdea,
        loading,
        error,
        loadSessionById,
        createAuthenticatedSession,
        updateSession,
        refreshSession,
        resetSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
